// Built-ins
import React, { useEffect, useMemo, useState } from "react";

// Modules / Functions
import axios from "axios";
import { FaPrint } from "react-icons/fa";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";

// Custom
import NavigationBar from "../../core/navigation_bar/navigation_bar";
import Footer from "../../core/footer/footer";
import { headers, pageHeader, loader, paginationOptions } from "../../global_vars";
import AutocompleteSelect from "../../common/AutocompleteSelect";

// API endpoints
const ALL_PROFESSIONALS = "https://ultima.icsgr.com/api/data_management/all_professionals/";
const ALL_PROFESSIONS = "https://ultima.icsgr.com/api/administration/all_professions/";

// Styling bits
const labelPillStyle = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: "10px",
  background: "#93ab3c",
  color: "#fff",
  fontSize: 12,
  fontWeight: 600,
  marginRight: 8,
  border: "1px solid #93ab3c",
};

function ProfessionalsReport() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [professionals, setProfessionals] = useState([]);
  const [professions, setProfessions] = useState([]);

  // Filters per spec: City, Profession, Reliability, Active
  const [filters, setFilters] = useState({
    city: "",
    profession: "",
    reliability: "",
    active: "",
  });

  useEffect(() => {
    const currentHeaders = { ...headers, Authorization: "Token " + localStorage.getItem("userToken") };
    async function load() {
      try {
        const [pRes, profRes] = await Promise.all([
          axios.get(ALL_PROFESSIONALS, { headers: currentHeaders }),
          axios.get(ALL_PROFESSIONS, { headers: currentHeaders }),
        ]);
        const allP = pRes?.data?.all_professionals || pRes?.data?.results || pRes?.data?.data || pRes?.data || [];
        const allProfs = profRes?.data?.all_professions || profRes?.data || [];
        setProfessionals(Array.isArray(allP) ? allP : []);
        setProfessions(Array.isArray(allProfs) ? allProfs : []);
        setIsLoaded(true);
      } catch (e) {
        setIsLoaded(true);
      }
    }
    load();
  }, []);

  function setF(key, val) {
    setFilters((prev) => ({ ...prev, [key]: val }));
  }

  const cityOptions = useMemo(() => {
    const s = new Set();
    (professionals || []).forEach((p) => { if (p.city?.title) s.add(p.city.title); });
    return Array.from(s);
  }, [professionals]);

  const filteredProfessionals = useMemo(() => {
    const city = filters.city || "";
    const professionId = filters.profession || "";
    const reliability = filters.reliability || "";
    const active = filters.active || "";

    function profPasses(p) {
      if (city && (p.city?.title || "") !== city) return false;
      if (professionId) {
        const id = p.profession?.profession_id || p.profession;
        if (String(id) !== String(professionId)) return false;
      }
      if (reliability && (p.reliability || "") !== reliability) return false;
      if (active && String(!!p.active) !== (active === "yes" ? "true" : "false")) return false;
      return true;
    }

    return (professionals || []).filter(profPasses);
  }, [professionals, filters]);

  const columns = [
    {
      dataField: "professional_id",
      text: "Professional ID",
      sort: true,
      formatter: (cell, row) => (
        <a href={`/data_management/professional/${row.professional_id}`} id="cell_link">{row.professional_id}</a>
      ),
    },
    { dataField: "fullname", text: "Full name", sort: true },
    { dataField: "profession.title", text: "Profession", sort: true, formatter: (c, r) => r.profession?.title || "" },
    { dataField: "city.title", text: "City", sort: true, formatter: (c, r) => r.city?.title || "" },
    { dataField: "reliability", text: "Reliability", sort: true },
    { dataField: "afm", text: "AFM", sort: true },
    { dataField: "bank_details", text: "Bank details", sort: true, formatter: (c) => c || "" },
    { dataField: "active", text: "Active", sort: true, formatter: (c, r) => (r.active ? "Yes" : "No") },
  ];

  return (
    <>
      <NavigationBar />
      <div className="mainContainer">
        {pageHeader("reports_professionals", "Reports — Professionals")}
        <div className="contentContainer">
          <div className="contentBody">
            {isLoaded ? (
              <>
                <style>{`.pillLink { color: inherit; text-decoration: none; } .pillLink:hover { color: #93ab3c; text-decoration: none; }`}</style>
                <div style={{ marginBottom: 16, background: '#fff', padding: 16, borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #eee' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px 16px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>City</span>
                      <AutocompleteSelect value={filters.city} onChange={(v) => setF('city', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        {cityOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
                      </AutocompleteSelect>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Profession</span>
                      <AutocompleteSelect value={filters.profession} onChange={(v) => setF('profession', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        {professions.map((pr) => (
                          <option key={pr.profession_id} value={pr.profession_id}>{pr.title}</option>
                        ))}
                      </AutocompleteSelect>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Reliability</span>
                      <AutocompleteSelect value={filters.reliability} onChange={(v) => setF('reliability', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </AutocompleteSelect>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Active</span>
                      <AutocompleteSelect value={filters.active} onChange={(v) => setF('active', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </AutocompleteSelect>
                    </div>
                  </div>
                </div>

                <ToolkitProvider bootstrap4 keyField="professional_id" data={filteredProfessionals} columns={columns} exportCSV={{ fileName: 'professionals_report.csv' }} search condensed>
                  {(props) => (
                    <div>
                      <BootstrapTable
                        {...props.baseProps}
                        pagination={paginationFactory(paginationOptions)}
                        defaultSorted={[{ dataField: 'fullname', order: 'asc' }]}
                        hover
                        bordered={false}
                        striped
                      />
                      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                        <button onClick={() => window.print()} style={{ marginLeft: 20, padding: '6px 10px', borderRadius: 6, border: '1px solid #444', background: '#444', color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaPrint /> Print / PDF
                        </button>
                      </div>
                    </div>
                  )}
                </ToolkitProvider>
              </>
            ) : (
              <div>{loader()}</div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default ProfessionalsReport;


